import React from 'react';
import { FileText, Calendar, MapPin, MoreVertical, Download, Trash2, Edit } from 'lucide-react';
import { cn } from '../utils/cn';
import { Badge } from '../atoms/Badge';
import { CountryFlag } from '../atoms/CountryFlag';
import type { DocumentCardProps } from '../types/DocumentCard.types';

export const DocumentCard: React.FC<DocumentCardProps> = ({
  title,
  documentType,
  country,
  countryCode,
  createdAt,
  status = 'final',
  onView,
  onEdit,
  onDownload,
  onDelete,
  className
}) => {
  const [showMenu, setShowMenu] = React.useState(false);

  return (
    <div
      className={cn(
        'bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-200 cursor-pointer relative',
        className
      )}
      onClick={onView}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-tiber-400 bg-opacity-10 rounded-lg">
            <FileText className="w-6 h-6 text-tiber-500" />
          </div>
          <div>
            <h3 className="font-semibold text-juridico text-lg">{title}</h3>
            <p className="text-sm text-titan">{documentType}</p>
          </div>
        </div>
        
        {/* Menu Button */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 hover:bg-romance-50 rounded"
          >
            <MoreVertical className="w-5 h-5 text-titan" />
          </button>
          
          {/* Dropdown Menu */}
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-tiber-200 py-1 z-10">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-romance-50 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
              )}
              {onDownload && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-romance-50 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-romance-50 flex items-center gap-2 text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="flex items-center gap-4 text-sm text-titan mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          <CountryFlag countryCode={countryCode} size="1em" title={country} />
          <span>{country}</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          <span>{createdAt}</span>
        </div>
      </div>

      {/* Status Badge */}
      <div>
        <Badge variant={status === 'final' ? 'success' : 'warning'}>
          {status === 'final' ? 'Finalizado' : 'Borrador'}
        </Badge>
      </div>
    </div>
  );
};

DocumentCard.displayName = 'DocumentCard';

